// Shared types, constants and helpers for the ServiceConnect marketplace.
import { supabase } from '../services/supabase';

export const C = {
  navy: '#0A1628', navyHover: '#111E35', gold: '#C9A84C', black: '#0A0A0A',
  white: '#FFFFFF', surface: '#F5F6F8', border: '#D0D5DD', borderLt: '#E8EBF0',
  text: '#0A0A0A', textSec: '#3D4552', muted: '#6B7280', mutedLt: '#9CA3AF',
  success: '#0A7C42', danger: '#C0222A', amber: '#92640A', teal: '#0D6E8A',
};

export const CATEGORIES = [
  'Legal', 'Fund Administration', 'OMS/PMS', 'Market Data',
  'Insurance', 'Accounting', 'Consulting',
];

export const REGIONS = [
  'Asia Pacific', 'Europe', 'North America', 'Latin America',
  'Middle East & Africa', 'UK', 'US', 'Global',
];

export const PRICE_MODELS = ['Fixed Fee', 'Per User', 'AUM bps', 'Custom'];

export const CURRENCIES = ['USD', 'GBP', 'EUR', 'CHF', 'SGD', 'HKD'];

export const ASSET_BUCKET = 'service-provider-assets';

export function catColor(cat: string): string {
  return ({
    'Legal': '#0A1628',
    'Fund Administration': '#0D6E8A',
    'OMS/PMS': '#92640A',
    'Market Data': '#0A7C42',
    'Insurance': '#C0222A',
    'Accounting': '#6B21A8',
    'Consulting': '#0369A1',
  } as Record<string, string>)[cat] || C.muted;
}

export interface ServiceProvider {
  id: string;
  owner_id: string | null;
  name: string;
  category: string;
  description: string | null;
  headquarters_country: string | null;
  headquarters_city: string | null;
  regions_covered: string[] | null;
  website: string | null;
  linkedin: string | null;
  founded_year: number | null;
  client_count: number | null;
  logo_url: string | null;
  pitch_deck_url: string | null;
  demo_video_url: string | null;
  account_status: string;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
}

export interface ServiceProduct {
  id: string;
  provider_id: string;
  name: string;
  description: string | null;
  price_from: string | null;
  price_to: string | null;
  price_model: string | null;
  price_currency: string | null;
  is_flagship: boolean;
  created_at: string;
}

export interface ServiceConnection {
  id: string;
  requester_firm_id: string | null;
  provider_id: string | null;
  status: string;
  requested_by: string | null;
  requested_at: string;
  approved_at: string | null;
}

export interface ServiceMessage {
  id: string;
  connection_id: string;
  sender_id: string | null;
  content: string;
  read_at: string | null;
  created_at: string;
}

export function hqLabel(p: Pick<ServiceProvider, 'headquarters_city' | 'headquarters_country'>): string {
  return [p.headquarters_city, p.headquarters_country].filter(Boolean).join(', ') || '-';
}

// Upload a file into the private asset bucket under the owner's folder and
// return the stored object path (NOT a URL, decks/videos/logos are streamed
// through short-lived signed URLs generated on read).
export async function uploadAsset(ownerId: string, kind: string, file: File): Promise<string> {
  const ext = file.name.split('.').pop() || 'bin';
  const path = `${ownerId}/${kind}-${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from(ASSET_BUCKET).upload(path, file, { upsert: true });
  if (error) throw new Error(error.message);
  return path;
}

// Turn a stored asset path into a signed URL for viewing. Accepts either a bare
// object path or a full URL (older rows) and returns it unchanged if already a URL.
export async function signedAssetUrl(path: string | null, expiresIn = 3600): Promise<string | null> {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  const { data } = await supabase.storage.from(ASSET_BUCKET).createSignedUrl(path, expiresIn);
  return data?.signedUrl ?? null;
}
